import { sedrApi, type SedrAuditEvent, type SedrFile, type SedrPhasesList, type SedrRequirement, type SedrRoomDetail } from '../../api/sedr';
import { getAppState } from '../../app';
import { downloadAndDecryptSedrEvidence, sedrEvidenceNeedsRoomKey } from '../../crypto/sedrEvidenceFile';
import { unwrapSedrRoomKey, wrapSedrRoomKeyForMember } from '../../crypto/sedrRoomKey';
import { formatFileSize } from '../../crypto/sharedFolderFile';
import { applyI18n, t } from '../../i18n';
import { bindLocaleRefresh } from '../../i18n/bindLocaleRefresh';
import { showConfirmDialog } from '../../ui/confirmDialog';
import { applyUploadProgress, hideUploadProgress } from '../../ui/uploadProgress';
import { showFilePreview } from '../../ui/filePreviewDialog';
import { showSedrImportHubDialog } from './sedrImportHubDialog';
import { renderSedrAccessPanel } from './sedrAccess';
import { renderSedrRoomSettingsPanel } from './sedrRoomSettingsPanel';
import {
  renderSedrCompliancePanel,
  type SedrCompliancePanelController,
} from './sedrCompliancePanel';
import {
  renderSedrDashboard,
  type CompliancePhaseId,
} from './sedrDashboard';
import { showSedrApplyFolderSetDialog } from './sedrApplyFolderSetDialog';
import { renderSedrEvidenceExplorer } from './sedrEvidenceExplorer';
import { uploadSedrEvidenceFiles } from './sedrEvidenceUpload';
import {
  sedrCryptoErrorMessageKey,
  sedrEvidenceOpenErrorMessageKey,
  sedrEvidenceUploadErrorMessageKey,
  sedrErrorMessageKey,
} from './sedrErrors';
import {
  canSedrAdminActions,
  canSedrManageAccess,
  canSedrMutateContent,
  isSedrAdmin,
} from './sedrPermissions';
import { renderSedrPhasesPanel } from './sedrPhasesPanel';
import { renderSedrReportSchedulePanel } from './sedrReportSchedulePanel';
import {
  buildSedrAuditLookup,
  mountSedrAuditExpandableTable,
} from './sedrAuditDetail';
import {
  isSedrLifecycleClosed,
  isSedrRoomReadOnly,
  loadSedrCategories,
  renderRoomStatusBanner,
  renderSetupGate,
  type SedrCategoryFilter,
  type SedrEvidenceFilter,
  type SedrHeaderBannerAction,
  type SedrLifecycleRequestFilter,
  type SedrMetricKind,
  type SedrOwnerFilter,
  type SedrRenewalFilter,
  type SedrStatusFilter,
} from './sedrUi';
import { sedrTabHelpTopic } from '../../help/defaultTopic';
import {
  applySidebarCollapsed,
  createActivePanelControl,
  effectiveSidebarCollapsed,
  readSidebarCollapsed,
  renderSedrRoomChromeActions,
  SEDR_MOBILE_LAYOUT_MQ,
  writeSidebarCollapsed,
  type SedrChromeTab,
} from './sedrRoomChrome';
import {
  buildSedrRoomUrl,
  pushAppUrl,
  readSedrRoomUrlState,
  type SedrRoomTab,
} from '../../app/navigationState';

type SedrTab = SedrRoomTab;

const DELETION_GRACE_DAYS = 7;

function currentUserId(): string {
  const state = getAppState();
  return state.kind === 'authenticated' ? state.session.userId : '';
}

function schedulePurgePreview(): string {
  return new Date(Date.now() + DELETION_GRACE_DAYS * 24 * 60 * 60 * 1000).toLocaleString();
}

function parseRoomId(): string | null {
  const match = window.location.pathname.match(/^\/sedr\/([^/]+)/);
  return match ? match[1] : null;
}

export function mountSedrRoom(host: HTMLElement, roomId: string): () => void {
  host.replaceChildren();
  return mountSedrRoomInner(host, roomId);
}

export function SedrRoomPage(): HTMLElement {
  const page = document.createElement('section');
  page.className = 'module-page module-page--sedr sedr-room-page';

  const roomId = parseRoomId();
  if (!roomId) {
    page.innerHTML = `<p class="error-text">${t('status.error')}</p>`;
    return page;
  }

  mountSedrRoomInner(page, roomId);
  return page;
}

function mountSedrRoomInner(page: HTMLElement, roomId: string): () => void {
  const initialUrl = readSedrRoomUrlState();
  const urlMatchesRoom = initialUrl?.roomId === roomId;

  let room: SedrRoomDetail | null = null;
  let roomKey: Uint8Array | null = null;
  let roomKeyErrorKey: string | null = null;
  let activeTab: SedrTab = urlMatchesRoom ? initialUrl!.tab : 'dashboard';
  let complianceStatusFilter: SedrStatusFilter = [];
  let complianceCategoryFilter: SedrCategoryFilter = 'all';
  let compliancePhaseId: CompliancePhaseId = null;
  let complianceSearch = '';
  let complianceCommentSearch = '';
  let complianceHideObsolete = true;
  let complianceObsoleteSubtypes: SedrStatusFilter | undefined;
  let complianceOwnerFilter: SedrOwnerFilter = 'all';
  let complianceRenewalFilter: SedrRenewalFilter = 'all';
  let complianceLifecycleRequestFilter: SedrLifecycleRequestFilter = 'all';
  let complianceEvidenceFilter: SedrEvidenceFilter = 'all';
  let evidenceSearch = '';
  let evidenceParentId: string | null = urlMatchesRoom ? initialUrl!.evidenceFolderId : null;
  let selectedRequirementId: string | null = urlMatchesRoom ? initialUrl!.requirementId : null;
  let selectedRequirementIds: string[] = selectedRequirementId ? [selectedRequirementId] : [];
  let requirements: SedrRequirement[] = [];
  let phasesList: SedrPhasesList = { phases: [], unassignedRequirementCount: 0 };
  let files: SedrFile[] = [];
  let auditEvents: SedrAuditEvent[] = [];
  let evidenceBusy = false;
  let pollTimer: number | null = null;
  let lastSyncFingerprint: string | null = null;
  let compliancePanel: SedrCompliancePanelController | null = null;
  let sidebarPreference = readSidebarCollapsed();
  let chromeActionsCleanup: (() => void) | null = null;
  const sidebarMq = window.matchMedia(SEDR_MOBILE_LAYOUT_MQ);
  const isSidebarCollapsed = () => effectiveSidebarCollapsed(sidebarPreference);

  const syncFingerprint = (r: SedrRoomDetail) =>
    `${r.lifecycleState}|${r.metrics.totalRequirements}|${r.metrics.uploadedEvidence}|${r.metrics.pendingApproval}|${r.metrics.approved}|${r.adminCount}`;

  page.innerHTML = `
    <div class="sedr-room-layout" id="sedr-room-body">
      <aside class="sedr-sidebar" id="sedr-sidebar"></aside>
      <div class="sedr-room-main files-browse-main">
        <div class="files-browse-chrome sedr-room-chrome">
          <div class="files-browse-chrome__identity">
            <h2 class="files-browse-chrome__title" id="sedr-room-title"></h2>
            <span class="sedr-room-chrome__org" id="sedr-room-org"></span>
            <span class="sedr-room-chrome__panel-slot" id="sedr-room-panel-slot"></span>
            <span id="sedr-room-banner-slot"></span>
          </div>
          <div class="files-browse-chrome__actions" id="sedr-room-actions"></div>
        </div>
        <div id="sedr-tab-content" class="sedr-room-main__body"></div>
      </div>
    </div>
  `;

  const layout = page.querySelector('#sedr-room-body') as HTMLElement;
  const sidebar = page.querySelector('#sedr-sidebar') as HTMLElement;
  const titleEl = page.querySelector('#sedr-room-title') as HTMLElement;
  const orgEl = page.querySelector('#sedr-room-org') as HTMLElement;
  const panelSlot = page.querySelector('#sedr-room-panel-slot') as HTMLElement;
  const bannerSlot = page.querySelector('#sedr-room-banner-slot') as HTMLElement;
  const actionsSlot = page.querySelector('#sedr-room-actions') as HTMLElement;
  const tabContent = page.querySelector('#sedr-tab-content') as HTMLElement;

  const isSetupIncomplete = () => room?.lifecycleState === 'SetupIncomplete';
  const isReadOnly = () => Boolean(room && isSedrRoomReadOnly(room.lifecycleState, room.archivedAt));
  const canWrite = () => Boolean(room && canSedrMutateContent(room));
  const canAdmin = () => Boolean(room && canSedrAdminActions(room));
  const canViewSettings = () => Boolean(room && isSedrAdmin(room.permission));

  const navigateRoom = (
    tab: SedrTab,
    options?: { requirementId?: string | null; evidenceFolderId?: string | null; replace?: boolean },
  ) => {
    activeTab = tab;
    if (options?.requirementId !== undefined) {
      selectedRequirementId = options.requirementId;
      selectedRequirementIds = selectedRequirementId ? [selectedRequirementId] : [];
      if (tab === 'compliance') {
        compliancePanel = null;
      }
    }
    if (options?.evidenceFolderId !== undefined) {
      evidenceParentId = options.evidenceFolderId;
    }
    pushAppUrl(
      buildSedrRoomUrl(roomId, tab, {
        requirementId: tab === 'compliance' ? selectedRequirementId : null,
        evidenceFolderId: tab === 'evidence' ? evidenceParentId : null,
      }),
      { replace: options?.replace },
    );
    renderSidebar();
    updateRoomHelpTip();
    void renderTab();
  };

  const applyUrlState = () => {
    const urlState = readSedrRoomUrlState();
    if (!urlState || urlState.roomId !== roomId) {
      return;
    }
    if (
      urlState.tab === activeTab
      && urlState.requirementId === selectedRequirementId
      && urlState.evidenceFolderId === evidenceParentId
    ) {
      return;
    }
    if (
      urlState.tab === 'compliance'
      && (urlState.tab !== activeTab || urlState.requirementId !== selectedRequirementId)
    ) {
      compliancePanel = null;
    }
    activeTab = urlState.tab;
    selectedRequirementId = urlState.requirementId;
    selectedRequirementIds = selectedRequirementId ? [selectedRequirementId] : [];
    evidenceParentId = urlState.evidenceFolderId;
    renderSidebar();
    updateRoomHelpTip();
    void renderTab();
  };

  const onRoomNav = () => applyUrlState();
  page.addEventListener('sedr:room-nav', onRoomNav);

  const ensureRoomKey = async (): Promise<Uint8Array> => {
    if (roomKey) return roomKey;
    if (!room) throw new Error('room_missing');
    try {
      roomKey = await unwrapSedrRoomKey(room.wrappedRoomKey, roomId);
      roomKeyErrorKey = null;
      return roomKey;
    } catch (cause) {
      roomKeyErrorKey = sedrCryptoErrorMessageKey(cause) ?? 'sedr.error.roomKeyUnavailable';
      throw cause;
    }
  };

  const tryLoadRoomKey = async (): Promise<boolean> => {
    if (roomKey) {
      roomKeyErrorKey = null;
      return true;
    }
    try {
      await ensureRoomKey();
      return true;
    } catch (cause) {
      console.error('SEDR room key unwrap failed', cause);
      return false;
    }
  };

  const getNavTabs = (): SedrChromeTab[] => {
    const setup = isSetupIncomplete();
    const canManageReports = Boolean(room?.canManageMembers) && !isReadOnly();
    const canManageSettings = canViewSettings();
    const tabs: SedrChromeTab[] = [
      { id: 'dashboard', labelKey: 'sedr.nav.dashboard', disabled: setup },
      { id: 'phases', labelKey: 'sedr.nav.phases', disabled: setup },
      { id: 'compliance', labelKey: 'sedr.nav.compliance', disabled: setup },
      { id: 'evidence', labelKey: 'sedr.nav.evidence', disabled: setup },
      { id: 'access', labelKey: 'sedr.nav.access', disabled: setup },
      ...(canManageReports ? [{ id: 'reports', labelKey: 'sedr.nav.reports', disabled: setup }] : []),
      { id: 'audit', labelKey: 'sedr.nav.audit', disabled: setup },
      ...(canManageSettings ? [{ id: 'settings', labelKey: 'sedr.nav.settings', disabled: setup }] : []),
    ];
    return tabs;
  };

  const selectNavTab = (tabId: string) => {
    if (isSetupIncomplete()) return;
    const tab = tabId as SedrTab;
    if (tab === 'compliance') {
      navigateRoom('compliance', { requirementId: null });
    } else if (tab === 'evidence') {
      navigateRoom('evidence', { evidenceFolderId: null });
    } else {
      navigateRoom(tab);
    }
  };

  const renderSidebar = () => {
    const sidebarCollapsed = isSidebarCollapsed();
    applySidebarCollapsed(layout, sidebar, sidebarCollapsed);
    if (sidebarCollapsed) {
      return;
    }
    const setup = isSetupIncomplete();
    const tabs = getNavTabs();
    sidebar.innerHTML = `
      <h2 class="sedr-sidebar__heading" data-i18n="sedr.nav.heading"></h2>
      <nav class="sedr-sidebar-nav" data-i18n-aria-label="sedr.nav.heading"></nav>
    `;
    const nav = sidebar.querySelector('.sedr-sidebar-nav') as HTMLElement;
    for (const tab of tabs) {
      const link = document.createElement('button');
      link.type = 'button';
      const isActive = setup ? tab.id === 'access' : activeTab === tab.id;
      link.className = `sedr-sidebar-link${isActive ? ' sedr-sidebar-link--active' : ''}`;
      link.dataset.tab = tab.id;
      link.setAttribute('data-i18n', tab.labelKey);
      if (tab.disabled) {
        link.disabled = true;
      } else {
        link.addEventListener('click', () => selectNavTab(tab.id));
      }
      nav.append(link);
    }
    applyI18n(sidebar);
  };

  const updateRoomChrome = () => {
    const topic = sedrTabHelpTopic(activeTab, { setupIncomplete: isSetupIncomplete() });
    page.setAttribute('data-help-context', topic);
    if (!room) return;
    const sidebarCollapsed = isSidebarCollapsed();
    titleEl.textContent = room.title;
    panelSlot.replaceChildren(createActivePanelControl({
      activeTab: isSetupIncomplete() ? 'access' : activeTab,
      tabs: getNavTabs(),
      navHidden: sidebarCollapsed,
      onSelect: selectNavTab,
    }));
    applyI18n(panelSlot);

    chromeActionsCleanup?.();
    const { fragment, cleanup } = renderSedrRoomChromeActions({
      fullscreenTarget: page,
      sidebarCollapsed,
      onToggleSidebar: () => {
        sidebarPreference = !sidebarPreference;
        writeSidebarCollapsed(sidebarPreference);
        renderSidebar();
        updateRoomChrome();
      },
      helpTopic: topic,
    });
    chromeActionsCleanup = cleanup;
    actionsSlot.replaceChildren(fragment);
    applyI18n(actionsSlot);
  };

  const updateRoomHelpTip = () => {
    updateRoomChrome();
  };

  const renderHeader = () => {
    if (!room) return;
    orgEl.textContent = room.clientOrganization ? `· ${room.clientOrganization}` : '';
    orgEl.hidden = !room.clientOrganization;
    bannerSlot.replaceChildren();
    const banner = renderRoomStatusBanner(room, {
      actions: buildDeletionBannerActions(room),
    });
    if (banner) bannerSlot.append(banner);
    updateRoomHelpTip();
  };

  const buildDeletionBannerActions = (current: SedrRoomDetail): SedrHeaderBannerAction[] | undefined => {
    if (!isSedrAdmin(current.permission) || current.archivedAt) return undefined;
    const me = currentUserId();

    if (current.lifecycleState === 'DeletionRequested') {
      if (current.deletionRequestedBy === me) {
        return [
          {
            labelKey: 'sedr.deletion.withdraw',
            className: 'button subtle',
            onClick: () => {
              void (async () => {
                try {
                  await sedrApi.cancelDeletion(roomId);
                  await refreshRoom();
                } catch (cause) {
                  console.error(t(sedrErrorMessageKey(cause)), cause);
                }
              })();
            },
          },
        ];
      }
      return [
        {
          labelKey: 'sedr.deletion.approve',
          className: 'button danger',
          onClick: () => {
            void (async () => {
              const confirmed = await showConfirmDialog({
                titleKey: 'sedr.deletion.approveTitle',
                messageKey: 'sedr.deletion.confirmApproveDetail',
                messageParams: {
                  title: current.title,
                  requester: current.deletionRequestedByName ?? '',
                  reason: current.deletionReason?.trim() || '—',
                  purgeAt: schedulePurgePreview(),
                },
                confirmLabelKey: 'sedr.deletion.approve',
                confirmClassName: 'primary',
              });
              if (!confirmed) return;
              try {
                await sedrApi.decideDeletion(roomId, true);
                await refreshRoom();
              } catch (cause) {
                console.error(t(sedrErrorMessageKey(cause)), cause);
              }
            })();
          },
        },
        {
          labelKey: 'sedr.deletion.reject',
          className: 'button subtle',
          onClick: () => {
            void (async () => {
              try {
                await sedrApi.decideDeletion(roomId, false);
                await refreshRoom();
              } catch (cause) {
                console.error(t(sedrErrorMessageKey(cause)), cause);
              }
            })();
          },
        },
      ];
    }

    if (current.lifecycleState === 'PendingDeletion') {
      return [
        {
          labelKey: 'sedr.deletion.cancel',
          className: 'button primary',
          onClick: () => {
            void (async () => {
              try {
                await sedrApi.cancelDeletion(roomId);
                await refreshRoom();
              } catch (cause) {
                console.error(t(sedrErrorMessageKey(cause)), cause);
              }
            })();
          },
        },
      ];
    }

    return undefined;
  };

  const renderSetup = () => {
    if (!room || !isSetupIncomplete()) return;
    tabContent.replaceChildren(renderSetupGate({
      onInviteAdmin: async (userId) => {
        const key = await ensureRoomKey();
        await sedrApi.inviteMember(roomId, {
          userId,
          permission: 'Admin',
          wrappedRoomKey: await wrapSedrRoomKeyForMember(roomId, key, userId),
        });
        await refreshRoom();
      },
      onDeleteRoom: async () => {
        if (!room) return;
        const confirmed = await showConfirmDialog({
          titleKey: 'sedr.deletion.deleteNowTitle',
          messageKey: 'sedr.deletion.confirmDeleteNow',
          messageParams: { title: room.title },
          confirmLabelKey: 'sedr.deletion.deleteNow',
        });
        if (!confirmed) return;
        await sedrApi.deleteRoom(roomId);
        history.pushState({}, '', '/sedr');
        window.dispatchEvent(new PopStateEvent('popstate'));
      },
    }));
  };

  const renderCompliance = () => {
    if (!room) return;
    if (selectedRequirementId) {
      const selected = requirements.find((r) => r.id === selectedRequirementId);
      if (selected && isSedrLifecycleClosed(selected.status)) {
        complianceHideObsolete = false;
      }
    }
    if (compliancePanel?.element.isConnected) {
      compliancePanel.update({
        requirements,
        phases: phasesList.phases,
        files,
      });
      return;
    }
    compliancePanel = renderSedrCompliancePanel({
      roomId,
      roomTitle: room.title,
      clientOrganization: room.clientOrganization,
      requirements,
      phases: phasesList.phases,
      files,
      canWrite: canWrite(),
      canAdmin: canAdmin(),
      showExport: true,
      showCreate: true,
      showImport: true,
      initial: {
        statusFilter: complianceStatusFilter,
        categoryFilter: complianceCategoryFilter,
        phaseId: compliancePhaseId,
        search: complianceSearch,
        commentSearch: complianceCommentSearch,
        hideObsolete: complianceHideObsolete,
        obsoleteSubtypes: complianceObsoleteSubtypes,
        ownerFilter: complianceOwnerFilter,
        renewalFilter: complianceRenewalFilter,
        lifecycleRequestFilter: complianceLifecycleRequestFilter,
        evidenceFilter: complianceEvidenceFilter,
        selectedRequirementIds: [...selectedRequirementIds],
      },
      onStateChange: (state) => {
        complianceStatusFilter = state.statusFilter;
        complianceCategoryFilter = state.categoryFilter;
        compliancePhaseId = state.phaseId;
        complianceSearch = state.search;
        complianceCommentSearch = state.commentSearch;
        complianceHideObsolete = state.hideObsolete;
        complianceObsoleteSubtypes = state.obsoleteSubtypes;
        complianceOwnerFilter = state.ownerFilter;
        complianceRenewalFilter = state.renewalFilter;
        complianceLifecycleRequestFilter = state.lifecycleRequestFilter;
        complianceEvidenceFilter = state.evidenceFilter;
        selectedRequirementIds = [...state.selectedRequirementIds];
        const nextId = state.selectedRequirementIds.length === 1
          ? state.selectedRequirementIds[0]!
          : null;
        const reqChanged = nextId !== selectedRequirementId;
        selectedRequirementId = nextId;
        if (reqChanged && activeTab === 'compliance') {
          pushAppUrl(
            buildSedrRoomUrl(roomId, 'compliance', { requirementId: selectedRequirementId }),
          );
        }
      },
      onOpenFile: (file, context) => openFile(file, undefined, context),
      onDownloadFile: (file) => downloadFile(file),
      onImport: () => {
        showSedrImportHubDialog({
          roomId,
          onDone: () => void refreshRoom(),
        });
      },
      onUploadFiles: canWrite() && !roomKeyErrorKey
        ? async (parentId, selected, onProgress) => {
            const key = await ensureRoomKey();
            const result = await uploadSedrEvidenceFiles({
              roomId,
              files,
              parentId,
              selected,
              roomKey: key,
              onProgress,
            });
            files = result.files;
            if (compliancePanel) {
              compliancePanel.update({
                requirements,
                phases: phasesList.phases,
                files,
              });
            }
            return result;
          }
        : undefined,
      onRequirementsChanged: async () => {
        requirements = await sedrApi.listRequirements(roomId);
        phasesList = await sedrApi.listPhases(roomId);
        files = await sedrApi.listFiles(roomId);
        if (room) room = await sedrApi.getRoom(roomId);
        renderCompliance();
      },
      onOpenRequirement: (requirementId) => {
        openCompliance({
          statusFilter: [],
          categoryFilter: 'all',
          requirementId,
          phaseId: null,
        });
      },
    });
    tabContent.replaceChildren(compliancePanel.element);
  };

  const openEvidenceBytes = async (file: SedrFile): Promise<Blob> => {
    const roomKeyBytes = sedrEvidenceNeedsRoomKey(file) ? await ensureRoomKey() : null;
    const bytes = await downloadAndDecryptSedrEvidence(file, roomKeyBytes);
    const copy = new Uint8Array(bytes);
    const buffer = copy.buffer.slice(copy.byteOffset, copy.byteOffset + copy.byteLength) as ArrayBuffer;
    return new Blob([buffer], { type: file.contentType || 'application/octet-stream' });
  };

  const openFile = async (
    file: SedrFile,
    onError?: (message: string) => void,
    context?: { gallery: SedrFile[] },
  ) => {
    if (!file.blobId) return;
    try {
      await sedrApi.recordFileOpened(roomId, file.id);
      const blob = await openEvidenceBytes(file);
      const gallery = context?.gallery?.filter((f) => Boolean(f.blobId));
      if (gallery && gallery.length > 1) {
        const initialIndex = Math.max(0, gallery.findIndex((f) => f.id === file.id));
        await showFilePreview(file.name, blob, file.contentType, {
          items: gallery.map((f) => ({
            id: f.id,
            fileName: f.name,
            contentType: f.contentType,
          })),
          initialIndex,
          loadItem: async (item) => {
            const target = gallery.find((f) => f.id === item.id);
            if (!target?.blobId) throw new Error('missing_gallery_file');
            await sedrApi.recordFileOpened(roomId, target.id);
            return openEvidenceBytes(target);
          },
        });
        return;
      }
      await showFilePreview(file.name, blob, file.contentType);
    } catch (cause) {
      console.error('SEDR preview failed', cause);
      onError?.(t(sedrEvidenceOpenErrorMessageKey(cause)));
    }
  };

  const downloadFile = async (file: SedrFile, onError?: (message: string) => void) => {
    if (!file.blobId) return;
    try {
      const blob = await openEvidenceBytes(file);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.append(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (cause) {
      console.error('SEDR download failed', cause);
      onError?.(t(sedrEvidenceOpenErrorMessageKey(cause)));
    }
  };

  const renderEvidence = async () => {
    await tryLoadRoomKey();

    const panel = document.createElement('div');
    panel.className = 'sedr-tab-panel sedr-tab-panel--evidence';

    if (roomKeyErrorKey) {
      const banner = document.createElement('p');
      banner.className = 'sedr-evidence-key-banner';
      banner.setAttribute('role', 'alert');
      banner.textContent = t(roomKeyErrorKey);
      panel.append(banner);
    }

    const explorerHost = document.createElement('div');
    explorerHost.className = 'sedr-evidence-explorer-host';
    panel.append(explorerHost);

    const showEvidenceError = (message: string) => {
      const errorEl = explorerHost.querySelector('#sedr-evidence-error') as HTMLParagraphElement | null;
      if (errorEl) {
        errorEl.hidden = false;
        errorEl.textContent = message;
      }
    };

    const visibleFiles = files;
    const cryptoBlocked = Boolean(roomKeyErrorKey);

    renderSedrEvidenceExplorer(explorerHost, {
      files: visibleFiles,
      readOnly: !canWrite(),
      allowUpload: !cryptoBlocked,
      formatSize: formatFileSize,
      initialParentId: evidenceParentId,
      initialSearch: evidenceSearch,
      isBusy: () => evidenceBusy,
      onFolderChange: (parentId) => {
        evidenceParentId = parentId;
        if (activeTab === 'evidence') {
          pushAppUrl(buildSedrRoomUrl(roomId, 'evidence', { evidenceFolderId: parentId }));
        }
      },
      onSearchChange: (query) => {
        evidenceSearch = query;
      },
      onOpenFile: (file) => openFile(file, showEvidenceError),
      onDownloadFile: (file) => downloadFile(file, showEvidenceError),
      onApplyFolderSet: canAdmin()
        ? () => {
            showSedrApplyFolderSetDialog({
              roomId,
              onDone: async () => {
                files = await sedrApi.listFiles(roomId);
                await renderEvidence();
              },
            });
          }
        : undefined,
      onNewFolder: async (parentId, name) => {
        await sedrApi.addFile(roomId, {
          name,
          isDirectory: true,
          parentId,
        });
        evidenceParentId = parentId;
        files = await sedrApi.listFiles(roomId);
        await renderEvidence();
      },
      onRename: async (file, newName) => {
        await sedrApi.patchFile(roomId, file.id, {
          name: newName,
          parentId: file.parentId ?? null,
        });
        files = await sedrApi.listFiles(roomId);
        await renderEvidence();
      },
      onMove: async (file, newParentId) => {
        await sedrApi.patchFile(roomId, file.id, {
          name: file.name,
          parentId: newParentId,
        });
        files = await sedrApi.listFiles(roomId);
        await renderEvidence();
      },
      onUploadFiles: (parentId, selected) => {
        if (selected.length === 0 || evidenceBusy || cryptoBlocked) return;
        const statusEl = explorerHost.querySelector('#sedr-evidence-status') as HTMLParagraphElement | null;
        const progressEl = explorerHost.querySelector('#sedr-evidence-progress') as HTMLProgressElement | null;
        const errorEl = explorerHost.querySelector('#sedr-evidence-error') as HTMLParagraphElement | null;
        if (errorEl) errorEl.hidden = true;
        evidenceBusy = true;
        evidenceParentId = parentId;
        void (async () => {
          try {
            const key = await ensureRoomKey();
            if (statusEl) {
              statusEl.hidden = false;
              statusEl.textContent = t('files.upload.uploading', { percent: 0 });
            }
            const result = await uploadSedrEvidenceFiles({
              roomId,
              files,
              parentId,
              selected,
              roomKey: key,
              onProgress: (progress) => {
                if (statusEl) applyUploadProgress(statusEl, progressEl, progress);
              },
            });
            hideUploadProgress(statusEl, progressEl);
            files = result.files;
            await renderEvidence();
          } catch (cause) {
            console.error('SEDR upload failed', cause);
            hideUploadProgress(statusEl, progressEl);
            const message = t(sedrEvidenceUploadErrorMessageKey(cause));
            if (errorEl) {
              errorEl.hidden = false;
              errorEl.textContent = message;
            } else {
              showEvidenceError(message);
            }
          } finally {
            evidenceBusy = false;
          }
        })();
      },
    });

    tabContent.replaceChildren(panel);
    applyI18n(panel);
  };

  const renderAudit = async () => {
    const panel = document.createElement('div');
    panel.className = 'sedr-tab-panel sedr-audit-panel';
    const [events, members, categories, reqs, phases, roomFiles] = await Promise.all([
      sedrApi.listAuditEvents(roomId),
      sedrApi.listMembers(roomId).catch(() => []),
      loadSedrCategories(roomId).catch(() => []),
      requirements.length > 0 ? Promise.resolve(requirements) : sedrApi.listRequirements(roomId),
      phasesList.phases.length > 0 ? Promise.resolve(phasesList) : sedrApi.listPhases(roomId),
      files.length > 0 ? Promise.resolve(files) : sedrApi.listFiles(roomId),
    ]);
    requirements = reqs;
    phasesList = phases;
    files = roomFiles;

    const lookup = buildSedrAuditLookup({
      requirements,
      phases: phasesList.phases,
      files,
      members,
      categories,
    });

    mountSedrAuditExpandableTable(panel, events, {
      lookup,
      onOpenRequirement: (requirementId) => {
        openCompliance({
          statusFilter: [],
          categoryFilter: 'all',
          requirementId,
          phaseId: null,
        });
      },
      onOpenPhase: (phaseId) => {
        openCompliance({
          statusFilter: [],
          categoryFilter: 'all',
          requirementId: null,
          phaseId,
        });
      },
      onOpenFile: (fileId) => {
        const file = files.find((f) => f.id === fileId);
        navigateRoom('evidence', {
          evidenceFolderId: file?.parentId ?? null,
        });
      },
    });
    tabContent.replaceChildren(panel);
  };

  const openCompliance = (opts?: {
    statusFilter?: SedrStatusFilter;
    categoryFilter?: SedrCategoryFilter;
    requirementId?: string | null;
    phaseId?: CompliancePhaseId;
  }) => {
    if (opts?.statusFilter !== undefined) complianceStatusFilter = opts.statusFilter;
    if (opts?.categoryFilter !== undefined) complianceCategoryFilter = opts.categoryFilter;
    if (opts?.phaseId !== undefined) compliancePhaseId = opts.phaseId;
    if (opts?.requirementId) {
      const selected = requirements.find((r) => r.id === opts.requirementId);
      if (selected && isSedrLifecycleClosed(selected.status)) {
        complianceHideObsolete = false;
      }
    }
    compliancePanel = null;
    navigateRoom('compliance', {
      requirementId: opts?.requirementId !== undefined ? opts.requirementId : selectedRequirementId,
    });
  };

  const openAudit = () => {
    navigateRoom('audit');
  };

  const openEvidence = () => {
    navigateRoom('evidence', { evidenceFolderId: evidenceParentId });
  };

  const handleMetricSelect = (metric: SedrMetricKind) => {
    switch (metric) {
      case 'total':
        openCompliance({ statusFilter: [], categoryFilter: 'all', requirementId: null, phaseId: null });
        break;
      case 'evidence':
        openEvidence();
        break;
      case 'pendingApproval':
        openCompliance({ statusFilter: ['UnderReview'], categoryFilter: 'all', requirementId: null, phaseId: null });
        break;
      case 'approved':
        openCompliance({ statusFilter: ['ApprovedDischarged'], categoryFilter: 'all', requirementId: null, phaseId: null });
        break;
    }
  };

  const renderDashboard = () => {
    if (!room) return;

    tabContent.replaceChildren(renderSedrDashboard({
      metrics: room.metrics,
      requirements,
      phasesList,
      auditEvents,
      canApprove: Boolean(room.canApprove),
      canManagePhases: canWrite(),
      onSelectMetric: handleMetricSelect,
      onOpenRequirement: (requirementId) => {
        openCompliance({ statusFilter: [], categoryFilter: 'all', requirementId, phaseId: null });
      },
      onViewAttentionAll: () => {
        openCompliance({
          statusFilter: room!.canApprove ? ['UnderReview'] : ['PendingUpload', 'Denied'],
          categoryFilter: 'all',
          requirementId: null,
          phaseId: null,
        });
      },
      onSelectPhase: (phaseId) => {
        openCompliance({
          statusFilter: [],
          categoryFilter: 'all',
          requirementId: null,
          phaseId: phaseId === null ? 'unassigned' : phaseId,
        });
      },
      onViewAudit: openAudit,
      onManagePhases: () => navigateRoom('phases'),
    }));
  };

  const renderPhases = () => {
    tabContent.replaceChildren(renderSedrPhasesPanel({
      roomId,
      phasesList,
      canWrite: canWrite(),
      onSelectPhase: (phaseId) => {
        openCompliance({
          statusFilter: [],
          categoryFilter: 'all',
          requirementId: null,
          phaseId: phaseId === null ? 'unassigned' : phaseId,
        });
      },
      onChanged: async () => {
        phasesList = await sedrApi.listPhases(roomId);
        renderPhases();
      },
    }));
  };

  const renderReports = async () => {
    if (!room) return;
    const canManage = Boolean(room.canManageMembers) && !isReadOnly();
    if (!canManage) {
      navigateRoom('dashboard', { replace: true });
      return;
    }
    const [members, guests] = await Promise.all([
      sedrApi.listMembers(roomId),
      sedrApi.listGuestInvites(roomId),
    ]);
    // renderSedrReportSchedulePanel mutates `host.className`, so we must not pass
    // the shared `tabContent` container or we lose its layout/padding styling.
    const host = document.createElement('div');
    tabContent.replaceChildren(host);
    await renderSedrReportSchedulePanel(host, {
      roomId,
      members,
      guests,
      readOnly: false,
    });
  };

  const renderTab = async () => {
    if (!room) return;
    if (isSetupIncomplete()) return;
    switch (activeTab) {
      case 'dashboard':
        requirements = await sedrApi.listRequirements(roomId);
        phasesList = await sedrApi.listPhases(roomId);
        files = await sedrApi.listFiles(roomId);
        auditEvents = await sedrApi.listAuditEvents(roomId);
        renderDashboard();
        break;
      case 'phases':
        phasesList = await sedrApi.listPhases(roomId);
        renderPhases();
        break;
      case 'compliance':
        requirements = await sedrApi.listRequirements(roomId);
        phasesList = await sedrApi.listPhases(roomId);
        files = await sedrApi.listFiles(roomId);
        renderCompliance();
        break;
      case 'evidence':
        files = await sedrApi.listFiles(roomId);
        await renderEvidence();
        break;
      case 'access':
        await renderSedrAccessPanel(tabContent, {
          roomId,
          roomTitle: room.title,
          canManage: canSedrManageAccess(room),
          roomType: room.roomType ?? 'ExternalAudit',
          allowGuestLinks: room.allowGuestLinks !== false,
          requireGuestTotp: Boolean(room.requireGuestTotp),
          getRoomKey: ensureRoomKey,
          onChanged: async () => {
            room = await sedrApi.getRoom(roomId);
          },
          onOpenReports: () => navigateRoom('reports'),
        });
        break;
      case 'reports':
        await renderReports();
        break;
      case 'settings':
        if (!canViewSettings()) {
          navigateRoom('dashboard', { replace: true });
          break;
        }
        await renderSedrRoomSettingsPanel(tabContent, {
          room,
          canManage: canViewSettings(),
          onRoomChanged: async () => {
            await refreshRoom();
          },
          onDeleted: () => {
            history.pushState({}, '', '/sedr');
            window.dispatchEvent(new PopStateEvent('popstate'));
          },
        });
        break;
      case 'audit':
        await renderAudit();
        break;
    }
  };

  const refreshRoom = async () => {
    room = await sedrApi.getRoom(roomId);
    lastSyncFingerprint = syncFingerprint(room);
    roomKey = null;
    roomKeyErrorKey = null;
    renderSidebar();
    renderHeader();
    renderSetup();
    await renderTab();
  };

  const startPolling = () => {
    if (pollTimer !== null) window.clearInterval(pollTimer);
    pollTimer = window.setInterval(() => {
      void (async () => {
        try {
          const latest = await sedrApi.getRoom(roomId);
          const fingerprint = syncFingerprint(latest);
          if (lastSyncFingerprint && fingerprint !== lastSyncFingerprint) {
            room = latest;
            lastSyncFingerprint = fingerprint;
            roomKey = null;
            roomKeyErrorKey = null;
            renderHeader();
            await renderTab();
          }
        } catch {
          // ignore poll errors
        }
      })();
    }, 15000);
  };

  void refreshRoom().then(startPolling);
  const unbindLocale = bindLocaleRefresh(() => {
    void refreshRoom();
  });
  const onSidebarMqChange = () => {
    renderSidebar();
    updateRoomChrome();
  };
  sidebarMq.addEventListener('change', onSidebarMqChange);

  return () => {
    if (pollTimer !== null) window.clearInterval(pollTimer);
    unbindLocale();
    sidebarMq.removeEventListener('change', onSidebarMqChange);
    chromeActionsCleanup?.();
    page.removeEventListener('sedr:room-nav', onRoomNav);
    page.replaceChildren();
  };
}
